import * as React from "react";
import { Route, Routes, useMatch, useNavigate, useParams } from "react-router-dom";

import { makeDocument } from "../../mock-data";
import { CompanyType } from "../../types/NotifierFormData";
import { WithExpressFormTemplate } from "@templates/ExpressFormTemplate";
import { NotAvailablePage } from "../Error/NotAvailable";
import { NotFoundPage } from "../Error/NotFoundPage";
import { LoadingPage } from "../LoadingPage";
import { AccountDetails } from "./AccountDetails";
import { Documents } from "./Documents";
import {
  getExpressData,
  getExpressSectionsData,
  getExpressSectionTargetDataByKey,
  putExpressData,
} from "./express-data";
import { ExpressPaymentPage } from "./ExpressPaymentPage";
import { PaymentFailedPage } from "../Error/PaymentFailedPageView";
import { KYC } from "./KYC";
import { NotifierDetails } from "./NotifierDetails";
import { PasswordSetupPage } from "./PasswordSetupPage";
import { Submit } from "./Submit/Submit";
import { ExpressFormSubmittedPage } from "./ExpressFormSubmittedPage";
import { ExpressPaymentSubmittedPage } from "./ExpressPaymentSubmittedPage";
import { lookup } from "@api/formApi";
import { ExpressAdditionalServicesPage } from "./ExpressAdditionalServices";

export const ExpressFormPage: React.FC<{}> = () => {
  const { slug } = useParams() as { slug: string };

  const { loading, error, serviceProvider } = useServiceProvider(slug);

  if (loading) return <LoadingPage />;
  if (error) return <NotFoundPage />;
  if (!serviceProvider) return <NotFoundPage />;
  return <Router serviceProvider={serviceProvider} />;
};

type RouterProps = {
  serviceProvider: {
    readonly companyName: string;
    readonly serviceProviderType: CompanyType;
    readonly inPartnership: boolean;
    readonly slug: string;
    readonly customForm?: string;
    readonly deathCertificateNeeded?: boolean;
    readonly willNeeded?: boolean;
    readonly logoUrl?: string;
    readonly expressFormLandingCopy?: string;
    readonly idDocumentNeeded?: boolean;
  };
};

interface ExpressComponentProps {
  serviceProvider: any;
  formMenuEntries: any;
  match: any;
  refresh?: any;
  navigate?: any;
  onSectionClick: any;
  submitted: boolean;
  acceptPayments?: boolean;
}
// const noPaymentSlugs = new Map([
//   ["aig", true],
//   ["eswater", true],
//   ["nwl", true],
//   ["octopus-energy", true],
//   ["portsmouth-water", true],
//   ["starling-bank", true],
//   ["which", true],
// ]);

const Router: React.FC<RouterProps> = ({ serviceProvider }) => {
  const match = useMatch("/express/:slug/*");
  if (!match) throw new Error("Unexpected");

  // const checkAcceptPayments = React.useCallback(() => {
  //   if (match.params.slug && noPaymentSlugs.has(match.params.slug)) {
  //     return false;
  //   } else {
  //     return true;
  //   }
  // }, [match.params.slug]);

  const acceptPayments = false;

  const navigate = useNavigate();

  const [count, setCount] = React.useState(0);

  const refresh = React.useCallback(() => {
    setCount((c) => c + 1);
  }, [setCount]);

  const submitted = React.useMemo(() => {
    const data = getExpressData();
    return data && data.submitted;
  }, []);

  const paymentKey = `${match?.params.slug}-key`;
  const [paymentStatus, setPaymentStatus] = React.useState<boolean>(() => {
    // paid or unpaid
    const storedValue = sessionStorage.getItem(paymentKey);
    return storedValue === "true" ? true : false;
  });
  const markPaymentSuccessful = () => {
    setPaymentStatus(true);
    sessionStorage.setItem(paymentKey, "true");
  };

  const formMenuEntries = React.useMemo(() => {
    const data = getExpressSectionsData();

    const entries = expressFormMenuEntries(
      serviceProvider,
      !!getExpressSectionTargetDataByKey("notifier")
    );
    if (!data) {
      return entries;
    }

    return entries.map(({ key, label }) => {
      if (!data[key]) {
        return { key, label };
      }
      const done = !!data[key].target;
      const error = data[key].source && !done;

      return { key, label, done, error };
    });
  }, [serviceProvider, count]);

  const onSectionClick = React.useCallback(
    (section) => {
      refresh();
      navigate(`${match.pathnameBase}/${section}`);
    },
    [match.pathnameBase, navigate, refresh]
  );

  const expressComponentProps: ExpressComponentProps = {
    serviceProvider: serviceProvider,
    formMenuEntries: formMenuEntries,
    match: match,
    refresh: refresh,
    navigate: navigate,
    onSectionClick: onSectionClick,
    submitted: submitted,
    acceptPayments: acceptPayments,
  };
  const afterSucessUrl = `${match.pathnameBase}/submitted`;
  const payementFailureGoBackUrl = `${match.pathnameBase}/payment`;
  const paymentPageProps = {
    serviceProvider: serviceProvider,
    isSubmitted: submitted,
    paymentStatus: paymentStatus,
    match: match,
  };

  return (
    <Routes>
      <Route path="account" element={GetAccountsComponent(expressComponentProps)} />
      <Route path="notifier" element={GetNotiferComponent(expressComponentProps)} />
      <Route path="documents" element={GetDocumentsComponent(expressComponentProps)} />
      <Route path="kyc" element={GetKYCComponent(expressComponentProps)} />
      <Route path="submit" element={GetSubmitComponent(expressComponentProps)} />

      <Route
        path="additional-services"
        element={GetAdditionalServicesComponent(expressComponentProps)}
      />

      {!acceptPayments && (
        <Route
          path="submitted"
          element={
            submitted ? (
              <ExpressFormSubmittedPage serviceProvider={serviceProvider} />
            ) : (
              NotSubmittedPage({ match })
            )
          }
        />
      )}

      {!acceptPayments || (
        <>
          <Route
            path="submitted"
            element={GetPaymentsSubmittedPage({ serviceProvider, submitted, paymentStatus, match })}
          />

          <Route
            path="lLdcJP9tmsRfSePe"
            element={
              <PaymentSuccess markPaid={markPaymentSuccessful} redirectUrl={afterSucessUrl} />
            }
          />

          <Route
            path="payment-failure"
            element={<PaymentFailedPage goBackUrl={payementFailureGoBackUrl} />}
          />
          <Route path="payment" element={GetPaymentsPage(paymentPageProps)} />
        </>
      )}

      <Route path="password" element={<PasswordSetupPage nextPageURL="/form" />} />

      <Route path="/" element={<Root />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

const Root = () => {
  const match = useMatch("/express/:slug/*");
  const navigate = useNavigate();

  React.useEffect(() => {
    if (match) {
      navigate(`${match.pathnameBase}/account`);
    }
  }, [match, navigate]);

  return null;
};

export const useServiceProvider = (slug: string) => {
  const [{ serviceProvider, loading, error }, setState] = React.useState({
    serviceProvider: null as {
      readonly companyName: string;
      readonly serviceProviderType: CompanyType;
      readonly inPartnership: boolean;
      readonly slug: string;
      readonly deathCertificateNeeded?: boolean;
      readonly willNeeded?: boolean;
      readonly idDocumentNeeded?: boolean;
      readonly expressFormLandingCopy?: string;
    } | null,
    loading: true,
    error: null as Error | null,
  });

  React.useEffect(() => {
    lookup({ slug }).then(
      (sp) => {
        const data = getExpressData();
        if (data?.slug !== slug) {
          putExpressData({ slug });
        }
        setState({
          serviceProvider: sp,
          loading: false,
          error: null,
        });
      },
      (_error) => {
        setState({ serviceProvider: null, loading: false, error: new Error("Not found") });
      }
    );
  }, [slug]);

  return { serviceProvider, loading, error };
};

export const expressFormMenuEntries = (
  sp: {
    serviceProviderType: CompanyType;
    deathCertificateNeeded?: boolean;
    willNeeded?: boolean;
    idDocumentNeeded?: boolean;
  },
  notifierSubmitted: boolean
) => [
    sp.serviceProviderType === CompanyType.Insurance
      ? { key: "account", label: "Policy details" }
      : { key: "account", label: "Account details" },

    { key: "notifier", label: "Your details" },
    ...(sp.deathCertificateNeeded || sp.willNeeded ? [{ key: "documents", label: "Documents" }] : []),

    ...(sp.idDocumentNeeded && notifierSubmitted ? [{ key: "kyc", label: "Id verification" }] : []),

    { key: "submit", label: "Submit" },
  ];

const PaymentSuccess: React.FC<{
  markPaid: () => void;
  redirectUrl: string;
}> = ({ markPaid, redirectUrl }) => {
  const navigate = useNavigate();
  React.useEffect(() => {
    markPaid();
    navigate(redirectUrl, { replace: true });
  }, []);
  return null;
};

const GetPaymentsSubmittedPage: React.FC<{
  serviceProvider: any;
  submitted: any;
  paymentStatus: boolean;
  match: any;
}> = ({ serviceProvider, submitted, paymentStatus, match }) => {
  if (!submitted) return <NotSubmittedPage match={match} />;
  if (!paymentStatus) {
    return (
      <NotAvailablePage title="Not Available" message="You have not Done the payment">
        <div style={{ textAlign: "center" }}>
          <a href={`${match.pathnameBase}/payment`}> Go to Payments</a>
        </div>
      </NotAvailablePage>
    );
  }
  return <ExpressPaymentSubmittedPage serviceProvider={serviceProvider} />;
};

const GetPaymentsPage: React.FC<{
  serviceProvider: any;
  isSubmitted: any;
  paymentStatus: boolean;
  match: any;
}> = ({ serviceProvider, isSubmitted, paymentStatus, match }) => {
  const successUrl = `${match.pathnameBase}/lLdcJP9tmsRfSePe`;
  const paymentFailureUrl = `${match.pathnameBase}/payment-failure`;

  if (!isSubmitted) return <NotSubmittedPage match={match} />;

  if (isSubmitted && paymentStatus) {
    return (
      <NotAvailablePage title="No longer available" message="You have already Done the payment">
        <div style={{ textAlign: "center" }}>
          <a href={getExpressData().formUrl}> Go to Dashboard</a>
        </div>
      </NotAvailablePage>
    );
  }

  return (
    <ExpressPaymentPage
      serviceProvider={serviceProvider}
      successUrl={successUrl}
      paymentFailureUrl={paymentFailureUrl}
    />
  );
};

const AlreadySubmittedPage: React.FC<{ match: any }> = ({ match }) => {
  return (
    <NotAvailablePage title="No Longer Available" message="You have Already Submitted the Form">
      <div style={{ textAlign: "center" }}>
        <a href={`${match.pathnameBase}/submitted`}> Go to Submitted</a>
      </div>
    </NotAvailablePage>
  );
};

const NotSubmittedPage: React.FC<{ match: any }> = ({ match }) => {
  return (
    <NotAvailablePage title="Not  Available" message="You have Not Submitted the Form">
      <div style={{ textAlign: "center" }}>
        <a href={`${match.pathnameBase}/submit`}> Go to Forms</a>
      </div>
    </NotAvailablePage>
  );
};

const GetAccountsComponent: React.FC<ExpressComponentProps> = ({
  serviceProvider,
  formMenuEntries,
  match,
  refresh,
  navigate,
  onSectionClick,
  submitted,
}): JSX.Element => {
  if (submitted) return <AlreadySubmittedPage match={match} />;

  // const afterAccount = React.useCallback(() => {
  //   refresh();
  //   if (serviceProvider.deathCertificateNeeded) {
  //     navigate(`${match.pathnameBase}/documents`);
  //   } else {
  //     navigate(`${match.pathnameBase}/notifier`);
  //   }
  // }, [match.pathnameBase, navigate, refresh, serviceProvider.deathCertificateNeeded]);
  const afterAccount = React.useCallback(() => {
    refresh();
    navigate(`${match.pathnameBase}/notifier`);
  }, [match.pathnameBase, navigate, refresh]);

  return (
    <WithExpressFormTemplate
      noSave
      inPartnership={serviceProvider.inPartnership}
      logoURL={serviceProvider.logoUrl}
      companyName={serviceProvider.companyName}
      menuEntries={formMenuEntries}
      proc={(updateTemplate) => (
        <AccountDetails
          serviceProvider={serviceProvider}
          next={afterAccount}
          updateTemplate={updateTemplate}
          onSectionClick={onSectionClick}
          uploadedFileInfo={() => Promise.resolve(makeDocument())} // TODO
        />
      )}
    />
  );
};

const GetDocumentsComponent: React.FC<ExpressComponentProps> = ({
  serviceProvider,
  formMenuEntries,
  match,
  refresh,
  navigate,
  onSectionClick,
  submitted,
}): JSX.Element => {
  if (submitted) return <AlreadySubmittedPage match={match} />;

  // const afterDocuments = React.useCallback(() => {
  //   refresh();
  //   navigate(`${match.pathnameBase}/notifier`);
  // }, [match.pathnameBase, navigate, refresh]);

  const afterDocuments = React.useCallback(() => {
    refresh();
    if (serviceProvider.idDocumentNeeded) {
      navigate(`${match.pathnameBase}/kyc`);
    } else {
      navigate(`${match.pathnameBase}/submit`);
    }
  }, [serviceProvider, match.pathnameBase, navigate, refresh]);

  return (
    <WithExpressFormTemplate
      logoURL={serviceProvider.logoUrl}
      inPartnership={serviceProvider.inPartnership}
      companyName={serviceProvider.companyName}
      menuEntries={formMenuEntries}
      proc={(updateTemplate) => (
        <Documents
          serviceProvider={serviceProvider}
          next={afterDocuments}
          updateTemplate={updateTemplate}
          onSectionClick={onSectionClick}
          busy={false} // TODO
          remoteError={undefined} // TODO
          removeRemoteError={() => { }}
        />
      )}
    />
  );
};

const GetNotiferComponent: React.FC<ExpressComponentProps> = ({
  serviceProvider,
  formMenuEntries,
  match,
  refresh,
  navigate,
  onSectionClick,
  submitted,
}): JSX.Element => {
  if (submitted) return <AlreadySubmittedPage match={match} />;

  // const afterNotifier = React.useCallback(() => {
  //   refresh();
  //   if (serviceProvider.idDocumentNeeded) {
  //     navigate(`${match.pathnameBase}/kyc`);
  //   } else {
  //     navigate(`${match.pathnameBase}/submit`);
  //   }
  // }, [serviceProvider, match.pathnameBase, navigate, refresh]);

  const afterNotifier = React.useCallback(() => {
    refresh();
    navigate(`${match.pathnameBase}/documents`);
  }, [match.pathnameBase, navigate, refresh]);

  return (
    <WithExpressFormTemplate
      noSave
      inPartnership={serviceProvider.inPartnership}
      logoURL={serviceProvider.logoUrl}
      companyName={serviceProvider.companyName}
      menuEntries={formMenuEntries}
      proc={(updateTemplate) => (
        <NotifierDetails
          serviceProvider={serviceProvider}
          next={afterNotifier}
          updateTemplate={updateTemplate}
          onSectionClick={onSectionClick}
        />
      )}
    />
  );
};

const GetKYCComponent: React.FC<ExpressComponentProps> = ({
  serviceProvider,
  formMenuEntries,
  match,
  refresh,
  navigate,
  onSectionClick,
  submitted,
}): JSX.Element => {
  if (submitted) return <AlreadySubmittedPage match={match} />;

  const afterKYC = React.useCallback(() => {
    refresh();
    navigate(`${match.pathnameBase}/submit`);
  }, [match.pathnameBase, navigate, refresh]);

  return (
    <WithExpressFormTemplate
      noSave
      inPartnership={serviceProvider.inPartnership}
      logoURL={serviceProvider.logoUrl}
      companyName={serviceProvider.companyName}
      menuEntries={formMenuEntries}
      proc={(updateTemplate) => (
        <KYC next={afterKYC} updateTemplate={updateTemplate} onSectionClick={onSectionClick} />
      )}
    />
  );
};

const GetSubmitComponent: React.FC<ExpressComponentProps> = ({
  serviceProvider,
  formMenuEntries,
  match,
  onSectionClick,
  submitted,
}): JSX.Element => {
  if (submitted) return <AlreadySubmittedPage match={match} />;
  var afterSubmit = React.useCallback(() => {
    window.location.href = `${match.pathnameBase}/additional-services`;
  }, [match.pathnameBase]);

  return (
    <WithExpressFormTemplate
      noSave
      inPartnership={serviceProvider.inPartnership}
      logoURL={serviceProvider.logoUrl}
      companyName={serviceProvider.companyName}
      menuEntries={formMenuEntries}
      proc={(updateTemplate) => (
        <Submit
          serviceProvider={serviceProvider}
          next={afterSubmit}
          updateTemplate={updateTemplate}
          onSectionClick={onSectionClick}
          uploadedFileInfo={() => Promise.resolve(makeDocument())} // TODO
        />
      )}
    />
  );
};

const GetAdditionalServicesComponent: React.FC<ExpressComponentProps> = ({
  serviceProvider,
  formMenuEntries,
  match,
  onSectionClick,
  submitted,
  acceptPayments,
}): JSX.Element => {
  var afterSubmit: () => void = React.useCallback(() => {
    if (acceptPayments) {
      window.location.href = `${match.pathnameBase}/payment`;
    } else {
      window.location.href = `${match.pathnameBase}/submitted`;
    }
  }, [match.pathnameBase, acceptPayments]);

  return (
    <>
      <ExpressAdditionalServicesPage next={afterSubmit} serviceProvider={serviceProvider} />
    </>
  );
};
